import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import userSvg from 'assets/icons/user.svg';
import warningIcon from 'assets/icons/warning.svg';
import Auth from 'modules/Auth';
import { PermissionsEnum } from 'consts/permissions';
import Card from 'lib/Card';
import Button, { Color } from 'lib/Button';
import Modal from 'lib/Modal';
import Input from 'lib/Input';
import { cancelFunctions } from 'utils/cancelFunctions';

import { saveSetDetails, deleteCreativeSet } from 'store/pages/contentManagement/creativeSetDetails/actions';
import { changeCode } from 'store/pages/contentManagement/creativeSetDetails/reducer';
import { Store } from 'components/common/types/Store.types';
import { CreativeSetDetails } from 'components/common/types/Creative.types';
import VisualUnitDetails from './VisualUnitDetails';
import CreativeDetails from './CreativeDetails';

const SetDetails = (): React.ReactElement => {
  const navigate = useNavigate();

  const creativeSetDetails = useSelector((state: Store) => state.pages.contentManagement.creativeSetDetails);

  const panels = useSelector((state: Store) => state.pages.contentManagement.creativeSetDetails.panels);
  const isEditingDisabled = useSelector(
    (state: Store) => state.pages.contentManagement.creativeSetDetails.isEditingDisabled,
  );
  const isNewSet = useSelector((state: Store) => state.pages.contentManagement.creativeSetDetails.isNewSet);
  const isPanelView = useSelector((state: Store) => state.pages.contentManagement.creativeSetDetails.isPanelView);
  const visualUnitIds = useSelector((state: Store) => state.pages.contentManagement.creativeSetDetails.visualUnitIds);
  const id = useSelector((state: Store) => state.pages.contentManagement.creativeSetDetails.id);
  const code = useSelector((state: Store) => state.pages.contentManagement.creativeSetDetails.code);
  const dsp = useSelector((state: Store) => state.pages.contentManagement.creativeSetDetails.dsp);
  const advertiser = useSelector((state: Store) => state.pages.contentManagement.creativeSetDetails.advertiser);
  const brand = useSelector((state: Store) => state.pages.contentManagement.creativeSetDetails.brand);

  const [isSetNameOpen, setIsSetNameOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const isSaveDisabled = !panels.every((panel) => !!panel.creative?.id) || isEditingDisabled || !isPanelView;
  const arePanelsOrdered = panels.every((panel) => !!panel.order);
  const dispatch = useDispatch();

  const onSuccessCreate = (): void => {
    setIsSetNameOpen(false);
    navigate('/content-management/creative-sets');
  };

  const saveSet = (): void => {
    dispatch(
      saveSetDetails({
        cancelFunctions,
        data: { dsp, advertiser, brand, panels, code, visualUnitIds },
        id,
        ordered: arePanelsOrdered,
        onSuccess: onSuccessCreate,
      }),
    );
  };

  const saveActionButtons = (
    <>
      <Button
        dataTestId="cancel-button"
        color={Color.DANGER}
        onClick={() => setIsSetNameOpen(false)}
        isDisabled={isEditingDisabled}
        label="Cancel"
        ddActionName="Cancel save set"
      />
      <Button
        dataTestId="save-button"
        color={Color.PRIMARY}
        onClick={saveSet}
        isDisabled={isEditingDisabled || code === ''}
        label="Save"
        ddActionName="Save set"
      />
    </>
  );

  const deleteActionButtons = (
    <>
      <Button
        dataTestId="cancel-button"
        color={Color.DANGER}
        onClick={() => setIsDeleteOpen(false)}
        isDisabled={isEditingDisabled}
        label="Cancel"
        ddActionName="Cancel delete"
      />
      <Button
        dataTestId="continue-button"
        color={Color.PRIMARY}
        onClick={() =>
          dispatch(
            deleteCreativeSet({
              cancelFunctions,
              data: { id, code },
              onSuccess: () => navigate('/content-management/creative-sets/'),
            }),
          )
        }
        isDisabled={isEditingDisabled}
        label="Continue"
        ddActionName="Continue delete"
      />
    </>
  );

  return (
    <>
      <div data-test-id="set-details" className="grid md:col-auto lg:grid-cols-12 gap-6">
        <div className="lg:col-span-8">
          <Card header="Visual units">
            <VisualUnitDetails panels={panels} visualUnitIds={visualUnitIds} />
          </Card>
        </div>
        <div className="lg:col-span-4">
          <Card header="Creatives">
            <CreativeDetails creativeDetails={creativeSetDetails as CreativeSetDetails} />
          </Card>
        </div>
      </div>
      <div className={`flex ${isNewSet ? 'justify-end' : 'justify-between'}`}>
        {Auth.hasPermission(PermissionsEnum.CONTENT_REVIEW) && !isNewSet ? (
          <Button
            dataTestId="delete-button"
            color={Color.SECONDARY}
            onClick={() => setIsDeleteOpen(true)}
            isDisabled={isEditingDisabled}
            label="Delete"
          />
        ) : null}
        {Auth.hasPermission(PermissionsEnum.CONTENT_APPROVE) && isNewSet ? (
          <Button
            dataTestId="save-set-button"
            color={Color.PRIMARY}
            onClick={id ? saveSet : () => setIsSetNameOpen(true)}
            isDisabled={isSaveDisabled}
            classNames="mt-2"
            label="Save set"
          />
        ) : null}
      </div>
      <Modal
        isOpen={isSetNameOpen}
        icon={userSvg}
        title="Save Creative Set"
        actionButtons={saveActionButtons}
        dataTestId="set-name-modal"
      >
        <p className="sub-header-base text-neutral-950-opacity-60 mb-2.5">Set Name</p>
        <Input
          dataTestId="set-name-input"
          placeholder="Type your set name..."
          onChange={(value: string) => dispatch(changeCode(value.trim()))}
          value={code}
          isDisabled={isEditingDisabled}
        />
      </Modal>
      <Modal
        isOpen={isDeleteOpen}
        icon={warningIcon}
        title="Delete creative set"
        actionButtons={deleteActionButtons}
        dataTestId="delete-modal"
      >
        <div className="ml-8 body-base text-neutral-950-opacity-60 space-y-3">
          <p>Are you sure you want to delete?</p>
          <p>This creative set will be removed from all linked Visual units.</p>

          <p>Please note that this action could impact live deals.</p>
        </div>
      </Modal>
    </>
  );
};

export default SetDetails;
